/**
 * 학생 신청 페이지 — 모의고사·도시락 — 2026-06-22
 * - 모의고사: 열린 신청 건 신청/취소 (마감 잠금)
 * - 도시락: 정기 요일 구독 + 향후 7일 일별 신청/취소
 * 백엔드: /api/v1/applications/*  (학생 토큰)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Tabs, Tab, Button, Chip, Stack, Divider, CircularProgress, Radio, RadioGroup, FormControlLabel } from '@mui/material';
import { API_BASE_URL } from '../../../config/api';

const WD = ['일', '월', '화', '수', '목', '금', '토'];
const authHeader = (): Record<string, string> => {
  const t = localStorage.getItem('student_access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const api = (p: string) => `${API_BASE_URL}/api/v1/applications${p}`;
const fmtDeadline = (iso: string | null) => iso ? new Date(iso).toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' }) : '제한 없음';
const fmtWon = (n: number) => `${(n || 0).toLocaleString('ko-KR')}원`;

interface EventOption { label: string; price: number; }
interface OpenEvent {
  id: string; category: string; title: string; description: string | null;
  event_date: string | null; apply_deadline: string | null; locked: boolean; my_applied: boolean;
  options?: EventOption[]; my_selected_option?: string | null;
}
interface MealDay { date: string; weekday: number; offered: boolean; ordered: boolean; locked: boolean; menu_labels?: string[]; }
interface MenuType { key: string; label: string; price: number; image_url: string | null; category?: string; }
interface SlotState {
  slot: string; label: string; enabled: boolean;
  offered_weekdays: number[]; cutoff_time: string; cutoff_day_offset: number;
  menu_types: MenuType[]; my_menu_types: string[];
  subscription: { weekdays: number[]; menu_types?: string[] } | null; days: MealDay[];
}
interface MealState {
  enabled: boolean; monthly_menu_image_url: string | null; month: string; month_total: number;
  slots: Record<string, SlotState>;
}
const MEAL_SLOTS: [string, string][] = [['lunch', '점심'], ['dinner', '저녁']];
const imgUrl = (url: string | null) => (url ? `${API_BASE_URL}${url}` : null);

export default function ApplicationPage() {
  const [tab, setTab] = useState(0);
  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>신청</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>모의고사·도시락·컨설팅을 신청할 수 있어요.</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="📝 모의고사" />
        <Tab label="🍱 도시락" />
        <Tab label="🧑‍🏫 컨설팅" />
      </Tabs>
      {tab === 0 ? <ExamSection /> : tab === 1 ? <MealSection /> : <ConsultingSection />}
    </Box>
  );
}

/* ----------------------------- 모의고사 ----------------------------- */
function ExamSection() {
  const [events, setEvents] = useState<OpenEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  // 이벤트별 학생이 고른 과목(옵션 label)
  const [picked, setPicked] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(api('/open'), { headers: authHeader() });
      if (res.ok) {
        const list = ((await res.json()).events || []).filter((e: OpenEvent) => e.category === 'mock_exam');
        setEvents(list);
        // 이미 신청한 건은 고른 과목을 라디오에 반영
        const init: Record<string, string> = {};
        list.forEach((e: OpenEvent) => { if (e.my_selected_option) init[e.id] = e.my_selected_option; });
        setPicked(init);
      }
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (e: OpenEvent, action: 'order' | 'cancel') => {
    const hasOptions = (e.options || []).length > 0;
    const selected_option = picked[e.id] || null;
    if (action === 'order' && hasOptions && !selected_option) {
      alert('과목을 선택하세요');
      return;
    }
    setBusy(e.id);
    try {
      const res = await fetch(api(`/events/${e.id}`), { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader() }, body: JSON.stringify({ action, selected_option }) });
      if (res.ok) await load();
      else alert((await res.json()).detail || '처리 실패');
    } finally { setBusy(null); }
  };

  if (loading) return <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress /></Box>;
  if (events.length === 0) return <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>신청할 모의고사가 없습니다</Typography>;

  return (
    <Stack spacing={1.5}>
      {events.map((e) => {
        const options = e.options || [];
        const hasOptions = options.length > 0;
        const sel = picked[e.id] || '';
        const selPrice = options.find((o) => o.label === sel)?.price ?? 0;
        return (
        <Card key={e.id} variant="outlined">
          <CardContent>
            <Typography variant="h6" fontWeight={700}>{e.title}</Typography>
            {e.description && <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>{e.description}</Typography>}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              실시 {e.event_date || '미정'} · 마감 {fmtDeadline(e.apply_deadline)}
            </Typography>

            {/* 과목 선택 (사탐/과탐 등 택1) */}
            {hasOptions && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>과목 선택 (택1)</Typography>
                <RadioGroup
                  value={sel}
                  onChange={(_, v) => setPicked((p) => ({ ...p, [e.id]: v }))}
                >
                  {options.map((o) => (
                    <FormControlLabel
                      key={o.label}
                      value={o.label}
                      disabled={e.my_applied || e.locked || busy === e.id}
                      control={<Radio size="small" />}
                      label={`${o.label} · ${fmtWon(o.price)}`}
                    />
                  ))}
                </RadioGroup>
              </Box>
            )}

            <Box sx={{ mt: 1.5 }}>
              {e.my_applied ? (
                <Button variant="outlined" color="error" disabled={e.locked || busy === e.id} onClick={() => act(e, 'cancel')}>
                  {e.locked ? '마감됨 (신청완료)' : '신청 취소'}
                </Button>
              ) : (
                <Button variant="contained" disabled={e.locked || busy === e.id} onClick={() => act(e, 'order')}>
                  {e.locked ? '마감됨' : (hasOptions && sel ? `신청하기 (${fmtWon(selPrice)})` : '신청하기')}
                </Button>
              )}
              {e.my_applied && (
                <Chip size="small" color="success"
                  label={e.my_selected_option ? `신청완료 · ${e.my_selected_option}` : '신청완료'}
                  sx={{ ml: 1 }} />
              )}
            </Box>
          </CardContent>
        </Card>
        );
      })}
    </Stack>
  );
}

/* ----------------------------- 도시락 ----------------------------- */
function MealSection() {
  const [state, setState] = useState<MealState | null>(null);
  const [loading, setLoading] = useState(true);
  const [selMenus, setSelMenus] = useState<Record<string, string[]>>({ lunch: [], dinner: [] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(api('/meal'), { headers: authHeader() });
      if (res.ok) {
        const data: MealState = await res.json();
        setState(data);
        const sm: Record<string, string[]> = { lunch: [], dinner: [] };
        for (const [slot] of MEAL_SLOTS) {
          const sc = data.slots?.[slot];
          sm[slot] = sc?.my_menu_types || [];
        }
        setSelMenus(sm);
      }
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleMenu = (slot: string, key: string) =>
    setSelMenus((p) => {
      const list = p[slot] || [];
      return { ...p, [slot]: list.includes(key) ? list.filter((k) => k !== key) : [...list, key] };
    });

  const toggleDay = async (slot: string, d: MealDay) => {
    const sc = state?.slots?.[slot];
    const hasMenus = (sc?.menu_types?.length || 0) > 0;
    if (!d.ordered && hasMenus && (selMenus[slot]?.length || 0) === 0) { alert('메뉴를 선택하세요'); return; }
    setBusy(true);
    try {
      const res = await fetch(api('/meal/day'), { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader() }, body: JSON.stringify({ date: d.date, slot, action: d.ordered ? 'cancel' : 'order', menu_types: d.ordered ? undefined : (selMenus[slot] || []) }) });
      if (res.ok) await load();
      else alert((await res.json()).detail || '처리 실패');
    } finally { setBusy(false); }
  };

  if (loading) return <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress /></Box>;
  if (!state || !state.enabled) return <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>도시락 신청을 운영하지 않습니다</Typography>;

  const enabledSlots = MEAL_SLOTS.filter(([s]) => state.slots?.[s]?.enabled);
  if (enabledSlots.length === 0) return <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>운영 중인 시간대가 없습니다</Typography>;

  const monthLabel = state.month ? `${Number(state.month.slice(5, 7))}월` : '이번 달';

  const renderSlot = (slot: string, label: string) => {
    const sc = state.slots[slot];
    const menus = sc.menu_types || [];
    const sel = selMenus[slot] || [];
    const days = (sc.days || []).filter((d) => d.offered);
    return (
      <Box key={slot}>
        <Typography variant="h6" fontWeight={700} sx={{ mt: 1, mb: 1 }}>🍱 {label}</Typography>
        <Stack spacing={2}>
          {/* 메뉴 멀티선택 */}
          {menus.length > 0 && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>{label} 메뉴 선택 (여러 개 가능)</Typography>
                <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1.5 }}>
                  {menus.map((m) => {
                    const on = sel.includes(m.key);
                    return (
                      <Box key={m.key} onClick={() => toggleMenu(slot, m.key)}
                        sx={{ width: 150, cursor: 'pointer', borderRadius: 2, overflow: 'hidden',
                          border: 2, borderColor: on ? 'warning.main' : 'divider', bgcolor: on ? 'warning.50' : 'background.paper' }}>
                        <Box sx={{ width: '100%', height: 110, bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                          {m.image_url
                            ? <img src={imgUrl(m.image_url) || ''} alt={m.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <Typography variant="caption" color="text.secondary">사진 없음</Typography>}
                          {on && <Chip size="small" color="warning" label="선택" sx={{ position: 'absolute', top: 6, right: 6 }} />}
                        </Box>
                        <Box sx={{ p: 1 }}>
                          <Typography variant="body2" fontWeight={600}>{m.label}</Typography>
                          <Typography variant="caption" color="text.secondary">{fmtWon(m.price)}</Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>선택한 메뉴가 아래 날짜별 신청에 적용됩니다.</Typography>
              </CardContent>
            </Card>
          )}

          {/* 향후 7일 */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>{label} — 이번 주</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>마감 {sc.cutoff_time} ({sc.cutoff_day_offset === 0 ? '당일' : '전날'}) 까지 신청·취소 가능</Typography>
              <Divider sx={{ mb: 1 }} />
              <Stack spacing={1}>
                {days.map((d) => {
                  const [, mm, dd] = d.date.split('-');
                  const menuTxt = (d.menu_labels || []).join(', ');
                  return (
                    <Box key={d.date} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography>
                        {Number(mm)}/{Number(dd)} ({WD[d.weekday]})
                        {d.ordered && <Chip size="small" color="warning" label={menuTxt || '신청'} sx={{ ml: 1 }} />}
                      </Typography>
                      <Button size="small" variant={d.ordered ? 'outlined' : 'contained'} color={d.ordered ? 'error' : 'primary'}
                        disabled={d.locked || busy} onClick={() => toggleDay(slot, d)}>
                        {d.locked ? '마감' : d.ordered ? '취소' : '신청'}
                      </Button>
                    </Box>
                  );
                })}
                {days.length === 0 && <Typography color="text.secondary" variant="body2">제공 요일이 없습니다</Typography>}
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    );
  };

  return (
    <Stack spacing={2}>
      {/* 당월 누적 금액 (점심·저녁 합산) */}
      <Card variant="outlined" sx={{ bgcolor: 'warning.50' }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body1" fontWeight={600}>{monthLabel} 도시락 누적</Typography>
          <Typography variant="h5" fontWeight={800} color="warning.dark">{fmtWon(state.month_total)}</Typography>
        </CardContent>
      </Card>

      {/* 월 식단표 (점심·저녁 공통) */}
      {state.monthly_menu_image_url && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>{monthLabel} 식단표</Typography>
            <img src={imgUrl(state.monthly_menu_image_url) || ''} alt="식단표" style={{ width: '100%', maxWidth: 480, borderRadius: 8 }} />
          </CardContent>
        </Card>
      )}

      {enabledSlots.map(([slot, label]) => renderSlot(slot, label))}
    </Stack>
  );
}

/* ----------------------------- 컨설팅 ----------------------------- */
const capi = (p: string) => `${API_BASE_URL}/api/v1/consulting${p}`;

interface ConsultSlot {
  id: string; slot_date: string; weekday: number; weekday_label: string;
  start_time: string; end_time: string; capacity: number; booked_count: number;
  remaining: number | null; is_full: boolean; is_mine: boolean; locked: boolean;
}
interface ConsultProgram { id: string; title: string; description: string | null; slots: ConsultSlot[]; }

function ConsultingSection() {
  const [programs, setPrograms] = useState<ConsultProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(capi(''), { headers: authHeader() });
      if (res.ok) setPrograms((await res.json()).programs || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const book = async (s: ConsultSlot) => {
    if (!window.confirm(`${s.slot_date} (${s.weekday_label}) ${s.start_time}~${s.end_time} 신청할까요?\n※ 신청 후에는 학생 화면에서 취소할 수 없습니다.`)) return;
    setBusy(s.id);
    try {
      const res = await fetch(capi(`/slots/${s.id}/book`), { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader() } });
      if (res.ok) await load();
      else alert((await res.json().catch(() => ({}))).detail || '신청 실패');
    } finally { setBusy(null); }
  };

  if (loading) return <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress /></Box>;
  const visible = programs.filter((p) => p.slots.length > 0);
  if (visible.length === 0) return <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>신청할 컨설팅이 없습니다</Typography>;

  return (
    <Stack spacing={1.5}>
      {visible.map((p) => (
        <Card key={p.id} variant="outlined">
          <CardContent>
            <Typography variant="h6" fontWeight={700}>{p.title}</Typography>
            {p.description && <Typography variant="body2" color="text.secondary">{p.description}</Typography>}
            <Typography variant="caption" color="text.secondary">신청 후 취소는 원장님께 문의해 주세요.</Typography>
            <Divider sx={{ my: 1.5 }} />
            <Stack spacing={1}>
              {p.slots.map((s) => (
                <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap',
                  p: 1, border: '1px solid', borderColor: s.is_mine ? 'success.light' : 'divider', borderRadius: 1,
                  bgcolor: s.is_mine ? 'success.50' : s.locked ? 'grey.50' : 'transparent' }}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {s.slot_date} ({s.weekday_label}) {s.start_time}~{s.end_time}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      정원 {s.capacity}명 {s.remaining != null && !s.is_full ? `· ${s.remaining}자리 남음` : ''}
                    </Typography>
                  </Box>
                  {s.is_mine ? (
                    <Chip color="success" size="small" label="신청 완료" />
                  ) : s.is_full ? (
                    <Chip size="small" label="마감" sx={{ bgcolor: 'error.100', color: 'error.main' }} />
                  ) : s.locked ? (
                    <Chip size="small" label="마감" />
                  ) : (
                    <Button size="small" variant="contained" disabled={busy === s.id} onClick={() => book(s)}>신청</Button>
                  )}
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}
